import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Plus } from "lucide-react";
import Reveal from "./Reveal";
import { useCart } from "@/context/CartContext";

const G2 = "https://static.prod-images.emergentagent.com/jobs/8d9c6305-0a3a-4076-a118-1d0569e8a3c0/images/gen2-";

const META = {
  gift: { img: "https://customer-assets-jt897jd0.emergentagent.net/job_box-hub-7/artifacts/a35316d15eac7c21_IMG_20261001_170706_961%20%281%29.webp", spec: "RIGID · FOIL · 50 MOQ" },
  pizza: { img: "https://customer-assets-jt897jd0.emergentagent.net/job_box-hub-7/artifacts/1t1k1mzd_IMG_20261003_203559_125%401062280650.webp", spec: "E-FLUTE · FOOD-GRADE · VENTED" },
  cake: { img: "https://images.unsplash.com/photo-1759563876829-47c081a2afd9?q=80&w=1200&auto=format&fit=crop", spec: "CLEAR WINDOW · 300 MOQ" },
  packaging: { img: "https://images.unsplash.com/photo-1656543802898-41c8c46683a7?q=80&w=1200&auto=format&fit=crop", spec: "CUSTOM PRINT · E-COMMERCE" },
  corrugated: { img: "https://images.unsplash.com/photo-1624137527136-66e631bdaa0e?q=80&w=1200&auto=format&fit=crop", spec: "3-PLY → 5-PLY · HEAVY DUTY" },
  burger: { img: "https://customer-assets-jt897jd0.emergentagent.net/job_box-hub-7/artifacts/g84nafhm_IMG_20261002_191857_111%401062274912.webp", spec: "CLAMSHELL · FOOD-GRADE" },
  chalk: { img: "https://customer-assets-jt897jd0.emergentagent.net/job_box-hub-7/artifacts/c87696a844ee3af4_IMG_20261003_204107_973%20%281%29.webp", spec: "RETAIL PACK · FULL-COLOUR" },
  juice: { img: "https://customer-assets-jt897jd0.emergentagent.net/job_box-hub-7/artifacts/ef3c04379de66944_IMG_20261003_203733_190%20%281%29.webp", spec: "CUP CARRIER · SHIPPER" },
  takeaway: { img: "https://customer-assets-jt897jd0.emergentagent.net/job_box-hub-7/artifacts/b3b3475c2e0978ca_Screenshot_20260622-185215.webp", spec: "ROUND · CLEAR LID" },
  bags: { img: "https://customer-assets-jt897jd0.emergentagent.net/job_box-hub-7/artifacts/d8c178b49a33737b_IMG_20261101_181916_713.webp", spec: "SOS · ROPE HANDLE · FULL-PRINT" },
};

const SPANS = [
  "lg:col-span-7 lg:row-span-2",
  "lg:col-span-5",
  "lg:col-span-5",
  "lg:col-span-4",
  "lg:col-span-8",
  "lg:col-span-3",
  "lg:col-span-3",
  "lg:col-span-3",
  "lg:col-span-3",
  "lg:col-span-12",
];

function Card({ c, i, onAdd }) {
  const m = META[c.slug] || {};
  const span = SPANS[i] || "lg:col-span-4";

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (i % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative overflow-hidden rounded-2xl border border-mint/15 bg-moss/30 clip-corner ${span} min-h-[240px] lg:min-h-0`}
      data-testid={`bento-card-${c.slug}`}
    >
      <img
        src={m.img}
        alt={c.name}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/55 to-forest/5" />

      <div className="relative h-full flex flex-col justify-end p-6 sm:p-7">
        <p className="font-mono text-[10px] tracking-[0.25em] text-mintsoft/90 mb-2">{m.spec}</p>
        <div className="flex items-end justify-between gap-3">
          <div>
            <h3 className="font-display font-semibold text-2xl sm:text-3xl text-kraft leading-tight">
              {c.name}
            </h3>
            <p className="mt-2 max-w-sm text-sm text-kraft/65 leading-relaxed">{c.blurb}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <button
              onClick={onAdd}
              data-testid={`bento-add-${c.slug}`}
              title={`Add flagship ${c.name.toLowerCase()} to quote`}
              className="h-10 w-10 grid place-items-center rounded-full border border-mint/40 text-mint hover:bg-mint hover:text-forest transition-colors"
            >
              <Plus size={17} />
            </button>
            <Link
              to={`/shop?category=${c.slug}`}
              data-testid={`bento-browse-${c.slug}`}
              className="h-10 w-10 grid place-items-center rounded-full bg-mint text-forest hover:bg-kraft transition-colors"
            >
              <ArrowUpRight size={17} />
            </Link>
          </div>
        </div>
        <p className="mt-3 font-mono text-[10px] tracking-[0.2em] text-kraft/40 uppercase">
          {c.count} styles in stock
        </p>
      </div>
    </motion.div>
  );
}

export default function BentoCategories({ categories, products }) {
  const { add } = useCart();

  const onAdd = (slug) => {
    const p = products.find((x) => x.category === slug);
    if (p) add(p, 1, "");
  };

  return (
    <section id="categories" className="relative py-24 sm:py-32" data-testid="bento-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-mintsoft/90 mb-4">
            01 — The Range
          </p>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <h2 className="font-display font-semibold tracking-tight text-3xl sm:text-5xl text-kraft max-w-2xl leading-[1.02]">
              Ten box families.<br />One workshop.
            </h2>
            <p className="max-w-sm text-sm text-kraft/60 leading-relaxed">
              From a single birthday box to ten thousand export cartons — every family is
              made to your size, print and volume.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 auto-rows-[180px] sm:auto-rows-[210px]">
          {categories.map((c, i) => (
            <Card key={c.slug} c={c} i={i} onAdd={() => onAdd(c.slug)} />
          ))}
        </div>
      </div>
    </section>
  );
}
